<template>
  <div class="user-control-panel-view">
    <!-- Benutzerinformationen mit Glasmorphismus-Effekt -->
    <v-card class="user-card">
      <div class="card-header">
        <div class="title-container">
          <v-icon color="success" size="24" class="mr-2"
            >mdi-account-circle</v-icon
          >
          <h2 class="card-title">Benutzerinformationen</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-content">
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.loginname"
              label="Benutzername"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-account</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.mitarbeiternummer"
              label="Mitarbeiternummer"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small"
                  >mdi-badge-account-horizontal</v-icon
                >
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.vorname"
              label="Vorname"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small"
                  >mdi-card-account-details</v-icon
                >
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.nachname"
              label="Nachname"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small"
                  >mdi-card-account-details-outline</v-icon
                >
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.email"
              label="E-Mail"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-email</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="user.abteilung"
              label="Abteilung"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              readonly
              class="form-field"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-domain</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <!-- Neues Geburtstagsfeld mit Info-Icon für Sternzeichen -->
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="birthdaySettings.birthday"
              type="date"
              label="Geburtstag"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              class="form-field"
              hint="Format: TT.MM.JJJJ"
              persistent-hint
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-cake-variant</v-icon>
              </template>
              <template v-slot:append-inner v-if="birthdaySettings.birthday">
                <v-tooltip
                  location="bottom"
                  :open-delay="200"
                  :close-delay="100"
                  content-class="zodiac-tooltip"
                >
                  <template v-slot:activator="{ props }">
                    <v-icon
                      v-bind="props"
                      color="amber"
                      size="small"
                      class="info-icon"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <!-- Sternzeichen im Tooltip -->
                  <div class="zodiac-tooltip-content">
                    <div class="d-flex align-center">
                      <v-icon size="24" color="amber" class="me-2">{{ zodiacIcon }}</v-icon>
                      <span class="text-subtitle-1 font-weight-medium">{{ zodiacSign }}</span>
                    </div>
                    <div class="text-caption mt-1">{{ getBirthdayInfoText }}</div>
                  </div>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6" class="d-flex align-center">
            <v-checkbox
              v-model="birthdaySettings.birthday_public"
              label="Geburtstag öffentlich anzeigen"
              color="success"
              hide-details
              class="mt-3"
            >
              <template v-slot:prepend>
                <v-icon color="success">mdi-eye</v-icon>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>

        <!-- Speicherbutton für die Benutzerinformationen -->
        <v-btn
          color="success"
          block
          size="large"
          type="submit"
          :loading="savingBirthday"
          @click="saveBirthdaySettings"
          class="submit-btn mt-4"
        >
          <v-icon start>mdi-content-save</v-icon>
          Geburtstags-Einstellungen speichern
        </v-btn>
      </div>
    </v-card>

<!-- Neue Card: Arbeitszeit-Einstellungen -->
<v-card class="worktime-card mt-6">
      <div class="card-header">
        <div class="title-container">
          <v-icon color="success" size="24" class="mr-2">mdi-clock-outline</v-icon>
          <h2 class="card-title">Arbeitszeit-Einstellungen</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-content">
        <p class="setting-description mb-4">
          Hier können Sie Ihre wöchentliche Arbeitszeit definieren. Diese wird zur Berechnung von Arbeitstagen und Stundensätzen in der Auswertung verwendet.
        </p>

        <v-form ref="worktimeForm" @submit.prevent="saveWorkTimeSettings">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="workTimeSettings.weeklyHours"
                type="number"
                min="1"
                max="60"
                step="0.5"
                label="Wochenarbeitszeit (Stunden)"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                color="success"
                class="form-field"
                :rules="[v => !!v || 'Bitte geben Sie Ihre Wochenarbeitszeit an']"
                hint="Standard: 38,5 Stunden pro Woche"
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-time-five</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="workTimeSettings.workdaysPerWeek"
                type="number"
                min="1"
                max="7"
                step="0.5"
                label="Arbeitstage pro Woche"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                color="success"
                class="form-field"
                :rules="[v => !!v || 'Bitte geben Sie Ihre Arbeitstage pro Woche an']"
                hint="Standard: 5 Tage pro Woche"
                persistent-hint
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-calendar-week</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <div class="worktime-summary mt-4 mb-4">
            <div class="summary-item">
              <div class="summary-label">
                <v-icon color="success" size="small" class="mr-1">mdi-calculator-variant</v-icon>
                Stunden pro Arbeitstag:
              </div>
              <div class="summary-value">{{ hoursPerWorkday }} Stunden</div>
            </div>

            <div class="summary-item">
              <div class="summary-label">
                <v-icon color="success" size="small" class="mr-1">mdi-calendar-month</v-icon>
                Arbeitstage pro Monat (Ø):
              </div>
              <div class="summary-value">{{ workdaysPerMonth }} Tage</div>
            </div>

            <div class="summary-item highlight">
              <div class="summary-label">
                <v-icon color="success" size="small" class="mr-1">mdi-timer-outline</v-icon>
                Monatliche Arbeitszeit (Ø):
              </div>
              <div class="summary-value">{{ monthlyWorkHours }} Stunden</div>
            </div>
          </div>

          <v-btn
            color="success"
            block
            size="large"
            type="submit"
            :loading="savingWorktime"
            class="submit-btn"
          >
            <v-icon start>mdi-content-save</v-icon>
            Arbeitszeit-Einstellungen speichern
          </v-btn>
        </v-form>
      </div>
    </v-card>

    <!-- Passwortänderung mit Glasmorphismus-Effekt -->
    <v-card class="password-card mt-6">
      <div class="card-header">
        <div class="title-container">
          <v-icon color="success" size="24" class="mr-2">mdi-lock-reset</v-icon>
          <h2 class="card-title">Passwort ändern</h2>
        </div>
      </div>

      <v-divider class="card-divider"></v-divider>

      <div class="card-content">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="currentPassword"
              label="Aktuelles Passwort"
              type="password"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              class="form-field"
              autocomplete="current-password"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-lock-outline</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="newPassword"
              label="Neues Passwort"
              type="password"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              class="form-field"
              hint="Mindestens 8 Zeichen"
              persistent-hint
              autocomplete="new-password"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-lock</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="confirmPassword"
              label="Passwort bestätigen"
              type="password"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              color="success"
              class="form-field"
              autocomplete="new-password"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-lock-check</v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <div class="password-requirements mt-2 mb-4">
          <div class="requirement" :class="{ fulfilled: hasMinLength }">
            <v-icon size="small" :color="hasMinLength ? 'success' : 'grey'">
              {{ hasMinLength ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>Mindestens 8 Zeichen</span>
          </div>

          <div class="requirement" :class="{ fulfilled: hasUpperCase }">
            <v-icon size="small" :color="hasUpperCase ? 'success' : 'grey'">
              {{ hasUpperCase ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>Mindestens ein Großbuchstabe</span>
          </div>

          <div class="requirement" :class="{ fulfilled: hasLowerCase }">
            <v-icon size="small" :color="hasLowerCase ? 'success' : 'grey'">
              {{ hasLowerCase ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>Mindestens ein Kleinbuchstabe</span>
          </div>

          <div class="requirement" :class="{ fulfilled: hasSpecialChar }">
            <v-icon size="small" :color="hasSpecialChar ? 'success' : 'grey'">
              {{ hasSpecialChar ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>Mindestens ein Sonderzeichen</span>
          </div>

          <div class="requirement" :class="{ fulfilled: passwordsMatch }">
            <v-icon size="small" :color="passwordsMatch ? 'success' : 'grey'">
              {{ passwordsMatch ? "mdi-check-circle" : "mdi-circle-outline" }}
            </v-icon>
            <span>Passwörter stimmen überein</span>
          </div>
        </div>

        <v-btn
          color="success"
          block
          size="large"
          :loading="updating"
          @click="updatePassword"
          class="submit-btn"
        >
          <v-icon start>mdi-content-save</v-icon>
          Passwort aktualisieren
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();
const worktimeForm = ref(null);
const birthdayForm = ref(null);

// ======== Zustandsverwaltung ========
const user = ref({
  loginname: "",
  vorname: "",
  nachname: "",
  email: "",
  mitarbeiternummer: "",
  abteilung: "",
});

// Passwort-Zustände
const currentPassword = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const updating = ref(false);

// Neue Zustände für Arbeitszeit-Einstellungen
const workTimeSettings = ref({
  weeklyHours: 38.5,
  workdaysPerWeek: 5,
  lastUpdated: null
});
const savingWorktime = ref(false);

// Neue Zustände für Geburtstags-Einstellungen
const birthdaySettings = ref({
  birthday: "",
  birthday_public: false
});
const savingBirthday = ref(false);

// ======== Computed Properties ========
const hasMinLength = computed(() => newPassword.value.length >= 8);
const passwordsMatch = computed(
  () =>
    confirmPassword.value.length > 0 &&
    newPassword.value === confirmPassword.value
);

// Neue Passwort-Validierungen
const hasUpperCase = computed(() => /[A-Z]/.test(newPassword.value));
const hasLowerCase = computed(() => /[a-z]/.test(newPassword.value));
const hasSpecialChar = computed(() =>
  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(newPassword.value)
);

// Gesamtvalidierung für das Passwort
const passwordIsValid = computed(
  () =>
    hasMinLength.value &&
    hasUpperCase.value &&
    hasLowerCase.value &&
    hasSpecialChar.value
);

// Neue berechnete Eigenschaften für Arbeitszeit
const hoursPerWorkday = computed(() => {
  if (!workTimeSettings.value.workdaysPerWeek || workTimeSettings.value.workdaysPerWeek === 0) return 0;
  return (workTimeSettings.value.weeklyHours / workTimeSettings.value.workdaysPerWeek).toFixed(2);
});

const workdaysPerMonth = computed(() => {
  // Durchschnittlich 4.33 Wochen pro Monat (52 Wochen / 12 Monate)
  return (workTimeSettings.value.workdaysPerWeek * 4.33).toFixed(1);
});

const monthlyWorkHours = computed(() => {
  // Durchschnittlich 4.33 Wochen pro Monat (52 Wochen / 12 Monate)
  return (workTimeSettings.value.weeklyHours * 4.33).toFixed(1);
});

// Neue berechnete Eigenschaft für Geburtstags-Info
const getBirthdayInfoText = computed(() => {
  if (!birthdaySettings.value.birthday) return "";
  const birthday = new Date(birthdaySettings.value.birthday);
  const today = new Date();

  // Alter berechnen und das aktuelle Jahr berücksichtigen
  let age = today.getFullYear() - birthday.getFullYear();
  const m = today.getMonth() - birthday.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
    age--;
  }

  return `Sie sind ${age} Jahre alt.`;
});

// Sternzeichen berechnen
const zodiacSign = computed(() => {
  if (!birthdaySettings.value.birthday) return "";

  const birthday = new Date(birthdaySettings.value.birthday);
  const day = birthday.getDate();
  const month = birthday.getMonth() + 1; // JavaScript Monate beginnen bei 0

  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return "Wassermann";
  if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return "Fische";
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return "Widder";
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return "Stier";
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return "Zwillinge";
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return "Krebs";
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "Löwe";
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return "Jungfrau";
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return "Waage";
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return "Skorpion";
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return "Schütze";
  return "Steinbock"; // 22.12.-19.1.
});

// Icon für Sternzeichen
const zodiacIcon = computed(() => {
  const sign = zodiacSign.value;

  switch (sign) {
    case "Wassermann": return "mdi-zodiac-aquarius";
    case "Fische": return "mdi-zodiac-pisces";
    case "Widder": return "mdi-zodiac-aries";
    case "Stier": return "mdi-zodiac-taurus";
    case "Zwillinge": return "mdi-zodiac-gemini";
    case "Krebs": return "mdi-zodiac-cancer";
    case "Löwe": return "mdi-zodiac-leo";
    case "Jungfrau": return "mdi-zodiac-virgo";
    case "Waage": return "mdi-zodiac-libra";
    case "Skorpion": return "mdi-zodiac-scorpio";
    case "Schütze": return "mdi-zodiac-sagittarius";
    case "Steinbock": return "mdi-zodiac-capricorn";
    default: return "mdi-help-circle";
  }
});

// ======== API-Interaktionen ========
// Benutzerinformationen abrufen
const fetchUserData = async () => {
  try {
    // Zuerst prüfen, ob der User bereits im AuthStore vorhanden ist
    if (authStore.user) {
      const response = await axios.post(
        `${API_BASE_URL}admin/users.php`,
        { action: "get" },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      if (
        response.data.status === "success" &&
        response.data.users &&
        response.data.users.length > 0
      ) {
        // Wichtige Änderung: Eigenen Benutzer anhand der ID finden
        // Für normale Benutzer gibt die API nur eine Person zurück (sich selbst)
        // Für Admins werden alle Benutzer zurückgegeben - hier müssen wir den richtigen finden
        const currentUserId = authStore.user.id;
        const currentUser = response.data.users.find(
          (u) => u.id === currentUserId
        );

        // Falls der Benutzer gefunden wurde, dessen Daten verwenden
        // Ansonsten als Fallback den ersten Benutzer nehmen
        user.value = currentUser || response.data.users[0];
      } else {
        // Setze einen Default-Wert, um Fehler im Template zu vermeiden
        user.value = {
          loginname: "",
          vorname: "",
          nachname: "",
          email: "",
          mitarbeiternummer: "",
          abteilung: "",
        };
        toast.error("Fehler beim Abrufen der Daten.");
      }
    }
  } catch (error) {
    console.error("❌ Fehler bei fetchUserData:", error);
    toast.error("Fehler beim Laden der Nutzerdaten!");
  }
};

const fetchWorkTimeSettings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}user_worktime_settings.php`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      workTimeSettings.value = {
        ...workTimeSettings.value, // Standardwerte beibehalten
        ...response.data.settings,  // Mit Server-Daten überschreiben
      };
    }
  } catch (error) {
    console.error("❌ Fehler beim Laden der Arbeitszeit-Einstellungen:", error);
    // Keine Fehlermeldung anzeigen, da es sein kann, dass der Nutzer noch keine Einstellungen hat
  }
};

const saveWorkTimeSettings = async () => {
  // Form validieren
  const { valid } = await worktimeForm.value.validate();
  if (!valid) return;

  savingWorktime.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}user_worktime_settings.php`,
      workTimeSettings.value,
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Arbeitszeit-Einstellungen erfolgreich gespeichert");
      // Aktualisierte Daten vom Server laden
      workTimeSettings.value = {
        ...workTimeSettings.value,
        lastUpdated: response.data.lastUpdated || new Date().toISOString()
      };
    } else {
      toast.error(response.data.message || "Fehler beim Speichern der Einstellungen");
    }
  } catch (error) {
    console.error("❌ Fehler beim Speichern der Arbeitszeit-Einstellungen:", error);
    toast.error("Fehler beim Speichern der Arbeitszeit-Einstellungen");
  } finally {
    savingWorktime.value = false;
  }
};

const fetchBirthdaySettings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}user_birthday_settings.php`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      birthdaySettings.value = {
        ...birthdaySettings.value, // Standardwerte beibehalten
        ...response.data.settings,  // Mit Server-Daten überschreiben
      };
    }
  } catch (error) {
    console.error("❌ Fehler beim Laden der Geburtstags-Einstellungen:", error);
    // Keine Fehlermeldung anzeigen, da es sein kann, dass der Nutzer noch keine Einstellungen hat
  }
};

const saveBirthdaySettings = async () => {
  // Form validieren
  if (birthdayForm.value) {
    const { valid } = await birthdayForm.value.validate();
    if (!valid) return;
  }

  savingBirthday.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}user_birthday_settings.php`,
      birthdaySettings.value,
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Geburtstags-Einstellungen erfolgreich gespeichert");
    } else {
      toast.error(response.data.message || "Fehler beim Speichern der Einstellungen");
    }
  } catch (error) {
    console.error("❌ Fehler beim Speichern der Geburtstags-Einstellungen:", error);
    toast.error("Fehler beim Speichern der Geburtstags-Einstellungen");
  } finally {
    savingBirthday.value = false;
  }
};

// Passwort ändern - aktualisieren mit allen Anforderungen
const updatePassword = async () => {
  // Alle Passwort-Anforderungen prüfen
  if (!passwordIsValid.value) {
    // Spezifische Fehlermeldung je nach nicht erfüllter Bedingung
    if (!hasMinLength.value) {
      toast.info("Das Passwort muss mindestens 8 Zeichen haben!");
    } else if (!hasUpperCase.value) {
      toast.info(
        "Das Passwort muss mindestens einen Großbuchstaben enthalten!"
      );
    } else if (!hasLowerCase.value) {
      toast.info(
        "Das Passwort muss mindestens einen Kleinbuchstaben enthalten!"
      );
    } else if (!hasSpecialChar.value) {
      toast.info("Das Passwort muss mindestens ein Sonderzeichen enthalten!");
    }
    return;
  }

  // Passwörter müssen übereinstimmen
  if (!passwordsMatch.value) {
    toast.info("Die eingegebenen Passwörter stimmen nicht überein!");
    return;
  }

  if (!currentPassword.value) {
    toast.info("Bitte aktuelles Passwort eingeben!");
    return;
  }

  updating.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}update_user_password.php`,
      {
        action: "update",
        current_password: currentPassword.value,
        password: newPassword.value,
      },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );

    if (response.data.status === "success") {
      toast.success("Passwort erfolgreich geändert!");
      currentPassword.value = "";
      newPassword.value = "";
      confirmPassword.value = "";
    } else {
      toast.error(response.data.message || "Fehler beim Ändern des Passworts!");
    }
  } catch (error) {
    console.error("❌ Fehler bei updatePassword:", error);
    toast.error(
      error?.response?.data?.message || "Fehler beim Ändern des Passworts!"
    );
  } finally {
    updating.value = false;
  }
};

onMounted(() => {
  fetchUserData();
  fetchWorkTimeSettings();
  fetchBirthdaySettings();
});
</script>

<style scoped>
/* ======== Hauptlayout und Basis-Styling ======== */
.user-control-panel-view {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Karten-Design mit Glasmorphismus ======== */
.user-card,
.password-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.user-card:hover,
.password-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

/* ======== Karten-Header ======== */
.card-header {
  display: flex;
  align-items: center;
  padding: 8px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  position: relative;
  z-index: 1;
}

.title-container {
  display: flex;
  align-items: center;
  flex: 1;
}

.card-title {
  font-size: 20px;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
  background: linear-gradient(45deg, #ffffff, rgba(255, 255, 255, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ======== Karten-Inhalte ======== */
.card-content {
  padding: 24px;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

/* ======== Formularfelder ======== */
.form-field {
  margin-bottom: 8px;
}

:deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

:deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

:deep(.v-field__field) {
  border-radius: 8px;
}

:deep(.v-field--variant-outlined .v-field__outline) {
  --v-field-border-opacity: 0.15;
}

:deep(.v-field--variant-outlined:hover .v-field__outline) {
  --v-field-border-opacity: 0.25;
}

:deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 0.35 !important;
}

:deep(.v-field--disabled) {
  opacity: 0.8;
}

:deep(.v-field__prepend-inner .v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
  opacity: 0.9;
}

.submit-btn {
  margin-top: 16px;
  letter-spacing: 0.5px;
  font-weight: 500;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  height: 50px;
  position: relative;
  overflow: hidden;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.6s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.submit-btn:hover:not(:disabled)::before {
  left: 100%;
}

/* ======== Passwort-Anforderungen ======== */
.password-requirements {
  padding: 16px;
  background: rgba(20, 20, 30, 0.4);
  border-radius: 12px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.requirement {
  display: flex;
  align-items: center;
  margin: 8px 0;
  opacity: 0.7;
  transition: all 0.3s ease;
}

.requirement.fulfilled {
  opacity: 1;
}

.requirement i {
  margin-right: 8px;
}

.requirement span {
  font-size: 0.9rem;
  margin-left: 8px;
}

/* ======== Animationen ======== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-card,
.password-card {
  animation: fadeIn 0.5s ease-out;
}

/* ======== Responsive Design ======== */
@media (max-width: 960px) {
  .card-header {
    padding: 16px 20px;
  }

  .card-content {
    padding: 16px 20px;
  }

  .card-title {
    font-size: 18px;
  }
}

@media (max-width: 600px) {
  .user-control-panel-view {
    padding-top: 16px;
  }

  .password-requirements {
    padding: 12px;
  }

  .requirement span {
    font-size: 0.85rem;
  }

  .submit-btn {
    margin-top: 24px;
  }
}

/* ======== Scrollbar-Styling ======== */
.user-control-panel-view::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.user-control-panel-view::-webkit-scrollbar-track {
  background: transparent;
}

.user-control-panel-view::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

.user-control-panel-view::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.25);
}

/* Neue Stile für die Arbeitszeit-Card */
.worktime-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.worktime-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

.setting-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.worktime-summary {
  background: rgba(20, 20, 30, 0.5);
  border-radius: 12px;
  padding: 16px;
  border-left: 3px solid rgba(76, 175, 80, 0.7);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
  padding: 6px 0;
}

.summary-item.highlight {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
}

.summary-label {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

.summary-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

@media (max-width: 600px) {
  .worktime-summary {
    padding: 12px;
  }

  .summary-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .summary-value {
    margin-top: 4px;
    margin-left: 24px;
  }
}

/* Neue Stile für die Geburtstags-Card */
.birthday-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.birthday-card:hover {
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
  transform: translateY(-2px);
}

.birthday-info {
  background: rgba(20, 20, 30, 0.5);
  border-radius: 12px;
  padding: 16px;
  border-left: 3px solid rgba(255, 193, 7, 0.7);
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
  padding: 6px 0;
}

.info-item.highlight {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
}

.info-label {
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
}

.info-value {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

@media (max-width: 600px) {
  .birthday-info {
    padding: 12px;
  }

  .info-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-value {
    margin-top: 4px;
    margin-left: 24px;
  }
}

/* Neue Styles für den Sternzeichen-Tooltip */
:deep(.zodiac-tooltip) {
  background: rgba(25, 25, 30, 0.95) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4);
  border-radius: 12px;
  padding: 8px 12px;
  max-width: 240px;
}

.zodiac-tooltip-content {
  padding: 4px 0;
}

.info-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  opacity: 0.8;
}

.info-icon:hover {
  transform: scale(1.15);
  opacity: 1;
}

/* Entfernen der vorherigen großen Sternzeichen-Anzeige */
.zodiac-display,
.zodiac-card,
.zodiac-name {
  display: none;
}

/* Animationen für Sternzeichen */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.zodiac-card:hover .v-avatar {
  animation: pulse 1.5s infinite;
}
</style>
